import React, { useState } from 'react';
import { DynamicResultTable } from '@css-xp/base';
import InfoPanel from '@css-xp/base/info-panel/src/info-panel';
import { RowType } from '@atlaskit/dynamic-table/dist/types/types';
import { DEFAULT_PAGE_SIZE } from '@css-xp/ppc-constants';
import { PartnerAccount } from '@css-xp/ppc-types';
import { SortDirection } from '@css-xp/ppc-graphtypes';
import { emptyStatePropForDiscountTab } from '@css-xp/ppc-common/src/state-props';
import { emptyStatePropDiscountTable, TableHeading } from '@css-xp/ppc-common';
import './discount-tab.scss';

interface DiscountTabProps {
  label: string;
  data: PartnerAccount;
  isDesignIteration1Enabled?: boolean;
}

enum DiscountTable {
  DESCRIPTION = 'Description',
  RATE = 'Rate',
}
type Discount = {
  label: string;
  percentage: number;
};

const wrapInCell = (node: React.ReactElement | string) => {
  return <div className="discount-cell">{node}</div>;
};

const sortLabels = (labels: Discount[], sortOrder: SortDirection) => {
  if (sortOrder === SortDirection.DESC) {
    return labels.sort((discount1, discount2) => (discount1.label < discount2.label ? 1 : -1));
  }

  return labels.sort((discount1, discount2) => (discount1.label > discount2.label ? 1 : -1));
};

const sortRates = (rates: Discount[], sortOrder: SortDirection) => {
  if (sortOrder === SortDirection.ASC) {
    return rates.sort((discount1, discount2) => discount1.percentage - discount2.percentage);
  }

  return rates.sort((discount1, discount2) => discount2.percentage - discount1.percentage);
};

const buildTableRows = (discounts: Discount[]) => {
  return discounts.map((discount) => {
    return {
      key: 'discount-rows',
      cells: [
        {
          key: DiscountTable.DESCRIPTION,
          content: wrapInCell(discount.label),
        },
        {
          key: DiscountTable.RATE,
          content: wrapInCell(discount.percentage + '%'),
        },
      ],
    };
  });
};

export const DiscountTab = ({ label, data, isDesignIteration1Enabled = false }: DiscountTabProps) => {
  const [sortOrder, setSortOrder] = useState<SortDirection>(SortDirection.DESC);
  const [sortKey, setSortKey] = useState<string>(
    isDesignIteration1Enabled ? DiscountTable.RATE : DiscountTable.DESCRIPTION
  );
  let availableDiscountsData = Object.values(data.discounts).map((discount: Discount) => discount);
  if (isDesignIteration1Enabled) {
    availableDiscountsData = sortRates(availableDiscountsData, sortOrder);
  } else {
    availableDiscountsData = sortLabels(availableDiscountsData, sortOrder);
  }
  const total = availableDiscountsData.length;

  const initialRows = buildTableRows(availableDiscountsData);
  const [tableRows, setTableRows] = useState<Array<RowType>>(initialRows);

  const headData = () => {
    if (total === 0) {
      return { cells: [] };
    }
    const cells = [
      {
        key: DiscountTable.DESCRIPTION,
        content: wrapInCell(DiscountTable.DESCRIPTION),
        isSortable: true,
        width: isDesignIteration1Enabled ? 20 : 75,
      },
      {
        key: DiscountTable.RATE,
        content: wrapInCell(DiscountTable.RATE),
        width: isDesignIteration1Enabled ? 80 : 25,
        isSortable: isDesignIteration1Enabled,
      },
    ];
    return {
      cells,
    };
  };

  const handleSorting = (data: any) => {
    if (isDesignIteration1Enabled) {
      let newSortedData = [];
      if (data.key === DiscountTable.DESCRIPTION) {
        newSortedData = sortLabels(availableDiscountsData, data.sortOrder);
      } else {
        newSortedData = sortRates(availableDiscountsData, data.sortOrder);
      }

      setTableRows(buildTableRows(newSortedData));
      setSortOrder(data.sortOrder);
      setSortKey(data.key);
    } else {
      const newSortedData = [...tableRows].reverse();
      setTableRows(newSortedData);
      sortOrder === SortDirection.ASC ? setSortOrder(SortDirection.DESC) : setSortOrder(SortDirection.ASC);
    }
  };

  const getDiscountMessage = () => {
    if (isDesignIteration1Enabled) {
      return <div>Apps follow the same discount rate as their parent products and are capped at 20%.</div>;
    }

    return (
      <div className="details-discount-message">
        <div>Atlassian and Marketplace apps: Apps will follow the same discount rate as their parent products.</div>
        <div>This discount will be capped at 20% and apply to both new and upgrade orders.</div>
      </div>
    );
  };

  const renderHeading = () => {
    return <TableHeading>{label}</TableHeading>;
  };

  const renderDiscountFlag = () => {
    return <InfoPanel type="information" message={getDiscountMessage()} />;
  };

  const renderBeforeTableContent = () => {
    return isDesignIteration1Enabled && !availableDiscountsData.length ? null : (
      <>
        {!isDesignIteration1Enabled && renderHeading()}
        {renderDiscountFlag()}
      </>
    );
  };

  return (
    <DynamicResultTable
      className="details-discount-table"
      emptyStateProps={isDesignIteration1Enabled ? emptyStatePropForDiscountTab : emptyStatePropDiscountTable}
      totalCount={total}
      currentPage={1}
      rowsPerPage={DEFAULT_PAGE_SIZE}
      isLoading={false}
      isPaginationEnabled={false}
      head={headData()}
      sortKey={sortKey}
      sortOrder={sortOrder}
      onSort={handleSorting}
      rows={tableRows}
      beforeTable={renderBeforeTableContent()}
    ></DynamicResultTable>
  );
};
